import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { userApi } from "../../lib/api";
import { useSession } from "../../lib/session";

/** Per account, so a shared browser asks each person once. */
const dismissKey = (userId: string) => `gp:security-nudge-dismissed:${userId}`;

function dismissed(userId: string): boolean {
  try {
    return localStorage.getItem(dismissKey(userId)) === "1";
  } catch {
    return false;
  }
}

/**
 * One quiet line for a signed-in account with no security questions (2026-10-25).
 *
 * Settings used to promise these accounts were never nagged. That held while the
 * questions only fed a survey; since password recovery runs on them, an account without
 * them has no way back in but the operator, and ~60% of accounts (everything made before
 * 2026-09-20) are in that position. So: one dismissible line on the control pages, gone
 * for good once dismissed or once the questions are set. Nothing is gated on it.
 *
 * It floats (2026-11-02): it can only appear once the session and then the profile have
 * answered, which is after the page has painted, and in the flow above the page it pushed
 * everything down when it did (CLS 0.07 on a phone's Feed, up to 0.29 on desktop). Fixed
 * to the bottom it moves nothing. Bottom-left on desktop, clear of Goon Fuel's corner;
 * above the Filters pill on a phone's Browse. The spacer, last in the flow (ControlApp
 * mounts this after the footer), lets the page's last lines and the footer scroll out from
 * under it; it is below the fold when it appears, so it moves nothing on screen either.
 * Browse's sticky tag column scrolls on its own, so it takes its own clearance while the
 * card shows (`[data-sq-nudge]` in index.css).
 */
export function SecurityQuestionsNudge() {
  const user = useSession((s) => s.user);
  const { pathname } = useLocation();
  const [missing, setMissing] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setMissing(false);
    if (!user || dismissed(user.id)) return;
    let cancelled = false;
    userApi
      .accountProfile()
      .then((r) => !cancelled && setMissing(r.profile.securityQuestionIds.length === 0))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user || !missing || hidden || pathname === "/settings") return null;

  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem(dismissKey(user.id), "1");
    } catch {
      /* private window: hidden for this page load is enough */
    }
  };

  return (
    <>
      <div aria-hidden="true" className="h-24 lg:h-20" />
      <div
        data-sq-nudge
        className={`fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-20 mx-auto flex max-w-md items-center gap-3 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)] px-3 py-2 text-xs text-zinc-300 shadow-2xl sm:px-4 lg:inset-x-auto lg:left-4 lg:mx-0 lg:w-[28rem] ${pathname === "/browse" ? "max-md:bottom-20" : ""}`}
      >
        <p className="min-w-0 flex-1">
          Set your security questions — they're how you get back in if you forget your password.
        </p>
        <Link to="/settings" className="shrink-0 font-medium text-[var(--color-accent-soft)] hover:underline">
          Set them
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded px-1 text-zinc-500 hover:text-zinc-200"
        >
          ✕
        </button>
      </div>
    </>
  );
}
